import React, { useState, useEffect } from 'react';
import './Controls.css';
import CustomPromptModal from './CustomPromptModal';

const Controls = ({
  settings,
  onSettingsChange,
  mode,
  onModeChange,
  sortBy,
  onSortChange,
  selectedCount,
  totalCount,
  onSelectAll,
  onProcess,
  onProcessAndUpload,
  disabled,
  csvFilePath,
  onOpenCSV,
  licenseInfo,
  iterations,
  setIterations
}) => {
  // State for crop presets - loaded from server
  const [cropPresets, setCropPresets] = useState({
    'custom': { width: '', height: '', label: 'Custom' }
  });
  const [presetsLoading, setPresetsLoading] = useState(true);
  
  // Local state for iterations input to handle immediate updates
  const [iterationsInput, setIterationsInput] = useState(iterations || 1);

  // Custom prompt state for AI Fill
  const [useSystemPrompt, setUseSystemPrompt] = useState(true);
  const [customPrompt, setCustomPrompt] = useState('');
  const [showPromptModal, setShowPromptModal] = useState(false);

  // Image alignment for AI Fill (where to position original image in canvas)
  const [cropAlignment, setCropAlignment] = useState('center');

  // Load crop presets from server on component mount
  useEffect(() => {
    loadCropPresets();
  }, []);
  
  // Sync local iterations state with prop
  useEffect(() => {
    setIterationsInput(iterations || 1);
  }, [iterations]);

  // Load custom prompt settings from localStorage on mount
  useEffect(() => {
    const savedUseSystemPrompt = localStorage.getItem('pixlsizer_useSystemPrompt');
    const savedCustomPrompt = localStorage.getItem('pixlsizer_customPrompt');

    if (savedUseSystemPrompt !== null) {
      setUseSystemPrompt(savedUseSystemPrompt === 'true');
    }
    if (savedCustomPrompt) {
      setCustomPrompt(savedCustomPrompt);
    }
  }, []);

  // Save custom prompt settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('pixlsizer_useSystemPrompt', useSystemPrompt.toString());
  }, [useSystemPrompt]);

  useEffect(() => {
    if (customPrompt) {
      localStorage.setItem('pixlsizer_customPrompt', customPrompt);
    }
  }, [customPrompt]);

  const loadCropPresets = async () => {
    try {
      // Check if we have cached presets (valid for 1 hour)
      const cached = localStorage.getItem('pixlsizer_crop_presets');
      if (cached) {
        const { presets, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        if (age < 60 * 60 * 1000) { // 1 hour cache
          setCropPresets({
            'custom': { width: '', height: '', label: 'Custom' },
            ...presets
          });
          setPresetsLoading(false);
          return;
        }
      }

      // Try to load from server
      const response = await fetch('https://www.pixlsizer.com/api/crop-presets');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.presets) {
          // Always keep 'custom' as first option
          setCropPresets({
            'custom': { width: '', height: '', label: 'Custom' },
            ...data.presets
          });
          // Cache the presets
          localStorage.setItem('pixlsizer_crop_presets', JSON.stringify({
            presets: data.presets,
            timestamp: Date.now()
          }));
        }
      }
    } catch (error) {
      console.log('Failed to load server presets, using defaults');
      // Fallback to default presets if server is unreachable
      setCropPresets({
        'custom': { width: '', height: '', label: 'Custom' },
        'tixr-flyer': { width: 400, height: 650, label: 'Portrait Flyer' },
        'tixr-mobile': { width: 770, height: 400, label: 'Landscape Flyer' },
        'social-og': { width: 1200, height: 630, label: 'Website Social OG' },
        'website-header': { width: 2200, height: 1080, label: 'Website Header' },
        'instagram-post': { width: 1080, height: 1080, label: 'Instagram Post' },
        'instagram-story': { width: 1080, height: 1920, label: 'Instagram Story' },
        'facebook-cover': { width: 1200, height: 630, label: 'Facebook Cover' }
      });
    } finally {
      setPresetsLoading(false);
    }
  };
  const handleSettingChange = (key, value) => {
    console.log(`=== SETTING CHANGE: ${key} = ${value} (${typeof value}) ===`);
    
    onSettingsChange(prev => {
      console.log('Previous settings:', JSON.stringify(prev, null, 2));
      
      const updated = { ...prev, [key]: value };
      
      // If enabling auto upscale and current format is AVIF, WebP, or ICON, switch to JPEG
      if (key === 'enableUpscaling' && value === true) {
        if (updated.format === 'avif' || updated.format === 'webp' || updated.format === 'icon') {
          console.log(`Auto-switching format from ${updated.format} to JPEG due to upscaling`);
          updated.format = 'jpeg';
          updated.quality = 72;
        }
      }
      
      // Handle crop preset changes
      if (key === 'cropPreset' && value !== 'custom') {
        const preset = cropPresets[value];
        console.log(`Applying preset ${value}:`, preset);
        updated.cropWidth = preset.width.toString();
        updated.cropHeight = preset.height.toString();
        console.log(`Set cropWidth: ${updated.cropWidth}, cropHeight: ${updated.cropHeight}`);
      }
      
      // If enabling AI fill and current format is AVIF, WebP, or ICON, switch to JPEG
      if (key === 'enableAIFill' && value === true) {
        if (updated.format === 'avif' || updated.format === 'webp' || updated.format === 'icon') {
          console.log(`Auto-switching format from ${updated.format} to JPEG due to AI Fill`);
          updated.format = 'jpeg';
          updated.quality = 72;
        }
      }
      
      // When format changes, set optimal quality for that format
      if (key === 'format') {
        switch (value) {
          case 'avif':
            updated.quality = 57;
            console.log('Setting AVIF quality to optimal 57%');
            break;
          case 'webp':
            updated.quality = 65;
            console.log('Setting WebP quality to optimal 65%');
            break;
          case 'jpeg':
            updated.quality = 72;
            console.log('Setting JPEG quality to optimal 72%');
            break;
          case 'png':
            updated.quality = 90;
            console.log('Setting PNG quality to optimal 90%');
            break;
          case 'icon':
            updated.quality = 100;
            updated.targetSize = '512'; // Auto-fill long edge with 512 for icons
            updated.shortEdgeSize = ''; // Clear short edge
            console.log('Setting ICON quality to 100% (SVG format) and size to 512x512');
            break;
        }
      }
      
      console.log('Updated settings:', JSON.stringify(updated, null, 2));
      console.log('=== SETTING CHANGE COMPLETE ===');
      
      return updated;
    });
  };

  // Handle custom prompt toggle
  const handleCustomPromptToggle = (enabled) => {
    setUseSystemPrompt(!enabled);

    // If enabling custom prompt, always show modal
    if (enabled) {
      setShowPromptModal(true);
    }
  };

  // Handle saving custom prompt
  const handleSaveCustomPrompt = (prompt) => {
    setCustomPrompt(prompt);
    // Also pass the custom prompt to parent component via settings
    onSettingsChange(prev => ({
      ...prev,
      customPrompt: prompt,
      useSystemPrompt: false
    }));
  };

  // Check if crop dimensions are set
  const hasCropDimensions = (settings.cropWidth || settings.cropHeight);

  return (
    <div className="controls glass-card">
      <div className="controls-content">
        {/* Image Processing Settings */}
        <div className="settings-section">
          <div className="section-title-row">
            {/* Mode selector */}
            <div className="mode-selector">
              <button
                className={`mode-button ${mode === 'resize' ? 'active' : ''}`}
                onClick={() => {
                  onModeChange('resize');
                  // Clear crop settings when switching to resize
                  handleSettingChange('cropWidth', '');
                  handleSettingChange('cropHeight', '');
                  handleSettingChange('cropPreset', 'custom');
                  handleSettingChange('enableAIFill', false);
                }}
                disabled={disabled}
              >
                RESIZE
              </button>
              <button
                className={`mode-button ${mode === 'crop' ? 'active' : ''}`}
                onClick={() => {
                  onModeChange('crop');
                  // Clear resize settings when switching to crop
                  handleSettingChange('targetSize', '');
                  handleSettingChange('shortEdgeSize', '');
                  handleSettingChange('enableUpscaling', false);
                }}
                disabled={disabled}
              >
                ASPECT CROP
              </button>
            </div>
            <h3 className="section-title no-margin">IMAGE PROCESSING SETTINGS</h3>

            {/* Custom Prompt Toggle - only show when AI Fill is enabled */}
            {mode === 'crop' && settings.enableAIFill && (
              <>
                <div className="custom-prompt-toggle header-toggle fade-in">
                  <span
                    className={`toggle-label ${!useSystemPrompt ? 'clickable' : ''}`}
                    onClick={() => {
                      // Only open modal if custom prompt is enabled (switch is ON)
                      if (!useSystemPrompt) {
                        setShowPromptModal(true);
                      }
                    }}
                  >
                    {useSystemPrompt ? 'Use Default Ai Prompt' : 'Edit Custom Ai Prompt'}
                  </span>
                  <label className="toggle-switch-wrapper">
                    <input
                      type="checkbox"
                      checked={!useSystemPrompt}
                      onChange={(e) => handleCustomPromptToggle(e.target.checked)}
                      disabled={disabled || !licenseInfo || !['pro', 'agency', 'partner'].includes(licenseInfo.tier)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                {/* Alignment selector - next to custom prompt */}
                <div className="alignment-selector header-alignment fade-in">
                  <span className="alignment-label">Anchor:</span>
                  <div className="alignment-grid">
                    <button
                      type="button"
                      className={`alignment-dot top ${cropAlignment === 'top' ? 'active' : ''}`}
                      onClick={() => {
                        setCropAlignment('top');
                        handleSettingChange('cropAlignment', 'top');
                      }}
                      disabled={disabled}
                      title="Anchor to top - expand downward"
                    />
                    <button
                      type="button"
                      className={`alignment-dot left ${cropAlignment === 'left' ? 'active' : ''}`}
                      onClick={() => {
                        setCropAlignment('left');
                        handleSettingChange('cropAlignment', 'left');
                      }}
                      disabled={disabled}
                      title="Anchor to left - expand rightward"
                    />
                    <button
                      type="button"
                      className={`alignment-dot center ${cropAlignment === 'center' ? 'active' : ''}`}
                      onClick={() => {
                        setCropAlignment('center');
                        handleSettingChange('cropAlignment', 'center');
                      }}
                      disabled={disabled}
                      title="Center - expand all directions"
                    />
                    <button
                      type="button"
                      className={`alignment-dot right ${cropAlignment === 'right' ? 'active' : ''}`}
                      onClick={() => {
                        setCropAlignment('right');
                        handleSettingChange('cropAlignment', 'right');
                      }}
                      disabled={disabled}
                      title="Anchor to right - expand leftward"
                    />
                    <button
                      type="button"
                      className={`alignment-dot bottom ${cropAlignment === 'bottom' ? 'active' : ''}`}
                      onClick={() => {
                        setCropAlignment('bottom');
                        handleSettingChange('cropAlignment', 'bottom');
                      }}
                      disabled={disabled}
                      title="Anchor to bottom - expand upward"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          
          <div className="settings-row-horizontal">
            {/* Conditionally render resize or crop controls */}
            {mode === 'resize' ? (
              <div className="toolbar-group mode-group">
                <div className="size-controls-wrapper">
                  {/* Long Edge Size */}
                  <div className="setting-group-horizontal compact">
                    <label className="setting-label resize-label">Long Edge</label>
                    <div className="size-input-container">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className={`glass-input size-input ${settings.shortEdgeSize ? 'input-disabled' : ''}`}
                        value={settings.targetSize}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 8192)) {
                            handleSettingChange('targetSize', value);
                          }
                        }}
                        onInput={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 8192)) {
                            handleSettingChange('targetSize', value);
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        placeholder="1024"
                        disabled={disabled || !!settings.shortEdgeSize}
                        autoComplete="off"
                        spellCheck={false}
                      />
                      <span className="input-unit">px</span>
                    </div>
                  </div>

                  {/* Short Edge Size */}
                  <div className="setting-group-horizontal compact">
                    <label className="setting-label resize-label">Short Edge</label>
                    <div className="size-input-container">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className={`glass-input size-input ${settings.targetSize ? 'input-disabled' : ''}`}
                        value={settings.shortEdgeSize}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 8192)) {
                            handleSettingChange('shortEdgeSize', value);
                          }
                        }}
                        onInput={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 8192)) {
                            handleSettingChange('shortEdgeSize', value);
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        placeholder="1024"
                        disabled={disabled || !!settings.targetSize}
                        autoComplete="off"
                        spellCheck={false}
                      />
                      <span className="input-unit">px</span>
                    </div>
                  </div>
                </div>

                {/* Ai Upscale */}
                <div className="upscale-control">
                  <label className={`upscale-checkbox ${!licenseInfo || !['pro', 'agency', 'partner'].includes(licenseInfo.tier) ? 'disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={settings.enableUpscaling || false}
                      onChange={(e) => handleSettingChange('enableUpscaling', e.target.checked)}
                      disabled={disabled || !licenseInfo || !['pro', 'agency', 'partner'].includes(licenseInfo.tier)}
                    />
                    <span>Ai Upscale</span>
                  </label>
                  <div className="info-tooltip">
                    <span className="info-icon">ℹ</span>
                    <div className="tooltip-content">
                      {!licenseInfo || !['pro', 'agency', 'partner'].includes(licenseInfo.tier) ? 
                        'Ai Upscale is available for Pro, Agency, and Partner licenses. Upgrade to use AI-powered image upscaling.' :
                        'AI-upscaled images beyond 5000 pixels could significantly slow down batch processing depending on your GPU. We recommend testing a few images before running your entire batch process. When upscaling logos, export to PNG for optimal results.'
                      }
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="toolbar-group mode-group">
                <div className="crop-section">
                  <label className="crop-label">AI CROP FILL</label>
                  <div className="crop-controls-horizontal">
                    <select
                      className="glass-select crop-preset-select compact"
                      value={settings.cropPreset}
                      onChange={(e) => handleSettingChange('cropPreset', e.target.value)}
                      disabled={disabled || presetsLoading}
                    >
                      {presetsLoading ? (
                        <option value="custom">Loading presets...</option>
                      ) : (
                        Object.entries(cropPresets).map(([key, preset]) => (
                          <option key={key} value={key}>{preset.label}</option>
                        ))
                      )}
                    </select>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="glass-input crop-input-compact"
                      value={settings.cropWidth}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 10000)) {
                          handleSettingChange('cropWidth', value);
                          handleSettingChange('cropPreset', 'custom');
                        }
                      }}
                      onFocus={(e) => e.target.select()}
                      placeholder="W"
                      title="Width in px — leave blank to keep each image's current width"
                      disabled={disabled}
                      autoComplete="off"
                      spellCheck={false}
                    />
                    <span className="crop-separator">×</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="glass-input crop-input-compact"
                      value={settings.cropHeight}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 10000)) {
                          handleSettingChange('cropHeight', value);
                          handleSettingChange('cropPreset', 'custom');
                        }
                      }}
                      onFocus={(e) => e.target.select()}
                      placeholder="H"
                      title="Height in px — leave blank to keep each image's current height"
                      disabled={disabled}
                      autoComplete="off"
                      spellCheck={false}
                    />
                    {hasCropDimensions && (
                      <>
                        <label className={`ai-fill-checkbox compact ${!licenseInfo || !['pro', 'agency', 'partner'].includes(licenseInfo.tier) ? 'disabled' : ''}`}>
                          <input
                            type="checkbox"
                            checked={settings.enableAIFill || false}
                            onChange={(e) => handleSettingChange('enableAIFill', e.target.checked)}
                            disabled={disabled || !licenseInfo || !['pro', 'agency', 'partner'].includes(licenseInfo.tier)}
                          />
                          <span>Ai Fill</span>
                        </label>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="toolbar-divider"></div>

            {/* Quality Section - Section 3 */}
            <div className="toolbar-group quality-section">
              <div className="setting-group-horizontal compact quality-group">
                <label className="setting-label">
                  Quality: {settings.quality}%
                </label>
                <input
                  type="range"
                  className="glass-slider quality-slider"
                  min="1"
                  max="100"
                  value={settings.quality}
                  onChange={(e) => handleSettingChange('quality', parseInt(e.target.value))}
                  disabled={disabled}
                />
              </div>
            </div>

            <div className="toolbar-divider"></div>

            {/* Format Section - Section 4 */}
            <div className="toolbar-group format-group">
              <div className="setting-group-horizontal compact">
                <label className="setting-label">Formats & File Types</label>
                <div className="format-options-horizontal">
                  {[
                    { value: 'avif', label: 'AVIF' },
                    { value: 'webp', label: 'WebP' },
                    { value: 'jpeg', label: 'JPEG' },
                    { value: 'png', label: 'PNG' },
                    { value: 'icon', label: 'Icons', hasTooltip: true }
                  ].map(format => {
                    const isDisabledFormat = disabled || 
                      ((settings.enableUpscaling || settings.enableAIFill) && (format.value === 'avif' || format.value === 'webp' || format.value === 'icon'));
                    
                    // If this format has a tooltip, wrap it in a div container
                    if (format.hasTooltip) {
                      return (
                        <div key={format.value} className="format-pill-wrapper">
                          <label 
                            className={`format-pill ${settings.format === format.value ? 'format-pill-active' : ''} ${isDisabledFormat ? 'format-pill-disabled' : ''} format-pill-with-tooltip`}
                            title={isDisabledFormat && (settings.enableUpscaling || settings.enableAIFill) ? 
                              'Format not supported with AI features' : ''}
                          >
                            <input
                              type="radio"
                              name="format"
                              value={format.value}
                              checked={settings.format === format.value}
                              onChange={(e) => handleSettingChange('format', e.target.value)}
                              disabled={isDisabledFormat}
                              className="format-radio-hidden"
                            />
                            {format.label}
                          </label>
                          <div className="info-tooltip format-tooltip">
                            <span className="info-icon">ℹ</span>
                            <div className="tooltip-content">
                              Choose Icons to export a complete icon bundle: 5 PNGs (512-32px), 5 ICOs (512-32px), 1 ICNS (Mac), and 1 SVG (vector). Images are center-cropped to square.
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Regular format pill without tooltip
                    return (
                      <label 
                        key={format.value} 
                        className={`format-pill ${settings.format === format.value ? 'format-pill-active' : ''} ${isDisabledFormat ? 'format-pill-disabled' : ''}`}
                        title={isDisabledFormat && (settings.enableUpscaling || settings.enableAIFill) ? 
                          'Format not supported with AI features' : ''}
                      >
                        <input
                          type="radio"
                          name="format"
                          value={format.value}
                          checked={settings.format === format.value}
                          onChange={(e) => handleSettingChange('format', e.target.value)}
                          disabled={isDisabledFormat}
                          className="format-radio-hidden"
                        />
                        {format.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="toolbar-divider"></div>

            {/* Suffix Section - Section 5 */}
            <div className="toolbar-group suffix-group">
              <div className="setting-group-horizontal compact">
                <label className="setting-label">Suffix</label>
                <div className="suffix-stack">
                  <div className="suffix-input-container">
                    <div className="info-tooltip">
                      <span className="info-icon">ℹ</span>
                      <div className="tooltip-content">
                        Optional suffix added to exported filenames (default: -pixl). Click Overwrite if you want to upscale and overwrite existing files to avoid duplicates — you'll be asked to confirm before processing.
                      </div>
                    </div>
                    <input
                      type="text"
                      className="glass-input suffix-input"
                      value={settings.overwriteOriginals ? '' : settings.prefix}
                      onChange={(e) => handleSettingChange('prefix', e.target.value)}
                      onInput={(e) => handleSettingChange('prefix', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      placeholder={settings.overwriteOriginals ? 'n/a' : '-pixl'}
                      disabled={disabled || settings.overwriteOriginals}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>

                  {/* Overwrite Originals (explained in the suffix info tooltip) */}
                  <div className="upscale-control overwrite-control">
                    <label className="upscale-checkbox">
                      <input
                        type="checkbox"
                        checked={settings.overwriteOriginals || false}
                        onChange={(e) => handleSettingChange('overwriteOriginals', e.target.checked)}
                        disabled={disabled}
                      />
                      <span>Overwrite</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Image Management */}
        <div className="management-section">
          <div className="management-row">
            <div className="sort-group">
              <label className="setting-label">Sort by</label>
              <select
                className="glass-select"
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                disabled={disabled}
              >
                <option value="modified">Date Modified</option>
                <option value="name">Name</option>
                <option value="size">Size (KB)</option>
                <option value="dimensions">Dimensions</option>
              </select>
            </div>

            <div className="selection-info">
              <span className="selection-count">
                {selectedCount} of {totalCount} selected
              </span>
              <div className="selection-actions">
                <button 
                  className="glass-button glass-button-secondary small"
                  onClick={() => onSelectAll(true)}
                  disabled={disabled || totalCount === 0}
                >
                  Select All
                </button>
                <button 
                  className="glass-button glass-button-secondary small"
                  onClick={() => onSelectAll(false)}
                  disabled={disabled || selectedCount === 0}
                >
                  Deselect All
                </button>
              </div>
            </div>

            <div className="process-actions">
              {/* Iterations field - only show when AI Fill is enabled */}
              {settings.enableAIFill && (
                <div className="iterations-field">
                  <label className="iterations-label"># of AI Iterations:</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="glass-input iterations-input"
                    value={iterationsInput}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      let num = 1;
                      if (value === '') {
                        num = 1;
                      } else {
                        num = parseInt(value, 10);
                        if (isNaN(num) || num < 1) {
                          num = 1;
                        } else if (num > 9) {
                          num = 9;
                        }
                      }
                      setIterationsInput(num);
                      setIterations(num);
                    }}
                    onBlur={() => {
                      // Ensure parent state is updated when focus is lost
                      setIterations(iterationsInput);
                    }}
                    disabled={disabled}
                    title="Number of times to run AI Fill for each image (max 9)"
                  />
                  {iterationsInput > 9 && (
                    <span className="iterations-warning">Max 9 allowed</span>
                  )}
                </div>
              )}
              <button 
                className="glass-button glass-button-green"
                onClick={onProcess}
                disabled={disabled || selectedCount === 0}
              >
                <svg className="button-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                </svg>
                {settings.overwriteOriginals ? 'Save and Overwrite' : 'Save to Folder'}
              </button>
              {licenseInfo?.features?.cloudUpload ? (
                <button 
                  className="glass-button"
                  onClick={onProcessAndUpload}
                  disabled={disabled || selectedCount === 0}
                >
                  <svg className="button-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path>
                  </svg>
                  Upload to Cloud
                </button>
              ) : (
                <button 
                  className="glass-button glass-button-disabled"
                  disabled={true}
                  title="Upgrade to Pro or Agency plan to unlock cloud export. Upload images directly to Amazon S3 with automatic CSV reports for easy Webflow integration."
                >
                  <svg className="button-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  Upload to Cloud
                </button>
              )}
              {csvFilePath && (
                <button 
                  className="glass-button glass-button-green csv-download-button"
                  onClick={onOpenCSV}
                  disabled={disabled}
                  title="Open CSV Report"
                >
                  <svg className="button-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="20" x2="18" y2="10"></line>
                    <line x1="12" y1="20" x2="12" y2="4"></line>
                    <line x1="6" y1="20" x2="6" y2="14"></line>
                  </svg>
                  Open Report
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Custom Prompt Modal */}
      <CustomPromptModal
        isOpen={showPromptModal}
        onClose={() => setShowPromptModal(false)}
        onSave={handleSaveCustomPrompt}
        initialPrompt={customPrompt}
      />
    </div>
  );
};

export default Controls;